import type {Dict, OverridedMixpanel} from 'mixpanel-browser'

const token = process.env.mixpanelToken

// Mixpanel populates its People profile fields from `$`-prefixed reserved
// properties. Map common generic trait keys so e.g. `email` shows up in the
// profile (and is usable for messaging) instead of as a custom property.
const RESERVED_TRAITS: Record<string, string> = {
  email: '$email',
  name: '$name',
  firstName: '$first_name',
  first_name: '$first_name',
  lastName: '$last_name',
  last_name: '$last_name',
  phone: '$phone',
  avatar: '$avatar',
}

const toMixpanelTraits = (traits: Dict): Dict =>
  Object.fromEntries(
    Object.entries(traits)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [RESERVED_TRAITS[key] ?? key, value]),
  )

// Loaded dynamically to keep it off the critical path. Exports stay sync and
// fire-and-forget: calls before the chunk resolves are queued, then replayed.
// This build fetches the session recorder from cdn.mxpnl.com only for sampled sessions. Not the
// README's `src/loaders` path: its recorder filename is an unreplaced build-time placeholder.
let mixpanel: OverridedMixpanel | null = null
let loading: Promise<void> | null = null
let loadFailed = false
const pending: Array<(mp: OverridedMixpanel) => void> = []

// Mirrors the "Send analytics events to providers" debug toggle
let trackingEnabled = true

const OPT_OUT_OPTIONS = {delete_user: false, clear_persistence: false}

const canLoad = () => typeof window !== 'undefined' && !!token && !loadFailed

export const initMixpanel = () => {
  if (loading || !canLoad()) {
    return
  }

  loading = import('mixpanel-browser/dist/mixpanel-with-async-recorder.cjs')
    .then(({default: mp}) => {
      mp.init(token as string, {
        batch_requests: true,
        record_sessions_percent: 5,
        record_heatmap_data: true,
        record_idle_timeout_ms: 600000,
      })

      mixpanel = mp

      if (!trackingEnabled) {
        mp.opt_out_tracking(OPT_OUT_OPTIONS)
      }

      while (pending.length > 0) {
        pending.shift()?.(mp)
      }
    })
    .catch(() => {
      // Analytics must never break the page. Give up permanently and drop what
      // we buffered, so a failed chunk load cannot grow the queue without bound.
      loadFailed = true
      pending.length = 0
    })
}

// Lazily kicks off loading before any call. React fires child effects before
// parent effects, so a tracking call from a deep component can run before the
// root layout's init effect
const run = (fn: (mp: OverridedMixpanel) => void) => {
  if (mixpanel) {
    fn(mixpanel)
    return
  }
  if (!canLoad()) {
    return
  }
  pending.push(fn)
  initMixpanel()
}

export const setMixpanelTrackingEnabled = (enabled: boolean) => {
  trackingEnabled = enabled
  if (!mixpanel) {
    return
  }
  if (enabled) {
    mixpanel.opt_in_tracking()
  } else {
    mixpanel.opt_out_tracking(OPT_OUT_OPTIONS)
  }
}

export const mixpanelTrack = (event: string, properties?: Dict) => {
  run((mp) => mp.track(event, properties))
}

export const mixpanelIdentify = (userId: string, traits?: Dict) => {
  run((mp) => {
    mp.identify(userId)
    if (traits) {
      mp.people.set(toMixpanelTraits(traits))
    }
  })
}

export const mixpanelRegister = (properties: Dict) => {
  run((mp) => mp.register(properties))
}

const ALIAS_FLAG_PREFIX = 'mp_aliased:'

// alias should happen only once per user, for rare cases there is localstorage guard
export const mixpanelAlias = (userId: string) => {
  if (!userId) {
    return
  }
  run((mp) => {
    const flagKey = `${ALIAS_FLAG_PREFIX}${userId}`
    if (localStorage.getItem(flagKey)) {
      return
    }
    mp.alias(userId)
    localStorage.setItem(flagKey, '1')
  })
}

export const mixpanelReset = () => {
  run((mp) => mp.reset())
}
